import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, LogIn, Loader2, RefreshCw, Copy, ExternalLink, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only switchboard that lists every host / pilot / demo account
 * and issues a one-click magic login to each. Mounted on `/me` when the
 * signed-in user is super-admin (`me@iamnaomithomas.com`).
 *
 * Iter 9.28: Split into two distinct sections - Pilots (live testers) and
 * Demos (canonical category templates). Adds an "Open" CTA that opens the
 * public-view link in a new tab (incognito-safe) without leaving /me.
 */
export const AdminSwitchboard = () => {
  const navigate = useNavigate();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/switchboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setHosts(data.hosts || []);
    } catch (err) {
      toast.error("Could not load switchboard: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSwitch = async (userId) => {
    setImpersonating(userId);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/admin/switchboard/${userId}/login`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Login failed");
      window.location.assign(`/auth/magic?token=${data.token}&admin_override=1`);
    } catch (err) {
      toast.error(String(err.message || err));
      setImpersonating(null);
    }
  };

  const generatePublicLink = async (userId) => {
    const token = localStorage.getItem("session_token");
    const res = await fetch(`${API}/api/admin/public-view-link/${userId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || "Failed to mint link");
    return data.public_url;
  };

  const handleOpenPublic = async (userId, name) => {
    setGenerating(userId);
    try {
      const url = await generatePublicLink(userId);
      // Open in new tab - works without clipboard permission
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success(`Opened ${name} in new tab`);
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setGenerating(null);
    }
  };

  const handleCopyPublic = async (userId, name) => {
    setGenerating(userId);
    try {
      const url = await generatePublicLink(userId);
      try {
        await navigator.clipboard.writeText(url);
        toast.success(`Public link for ${name} copied`);
      } catch (_) {
        // Fallback: open prompt so user can copy manually
        window.prompt(`Copy this public-view link for ${name}:`, url);
      }
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setGenerating(null);
    }
  };

  const filtered = useMemo(() => {
    return hosts.filter((h) => {
      if (!filter) return true;
      const needle = filter.toLowerCase();
      return (
        (h.name || "").toLowerCase().includes(needle) ||
        (h.email || "").toLowerCase().includes(needle) ||
        (h.events || []).some((e) => (e.name || "").toLowerCase().includes(needle)) ||
        (h.category || "").toLowerCase().includes(needle)
      );
    });
  }, [hosts, filter]);

  const pilots = filtered.filter((h) => h.is_pilot);
  const demos = filtered.filter((h) => h.is_demo && !h.is_pilot);

  const renderRow = (h) => (
    <li key={h.user_id} className="py-2.5" data-testid={`switchboard-host-row-${h.user_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{h.name}</span>
            {h.category && (
              <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-gray-100 text-gray-700 capitalize">
                {h.category}
              </span>
            )}
            {h.plan === "legacy_pilot_free" && (
              <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-emerald-100 text-emerald-700">Free pilot</span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{h.email}</p>
          {h.events?.length > 0 && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              {h.events.length} dashboard{h.events.length > 1 ? "s" : ""}:{" "}
              {h.events.map((e) => e.name || e.event_id).join(", ")}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => handleSwitch(h.user_id)}
          disabled={impersonating === h.user_id}
          data-testid={`switchboard-login-btn-${h.user_id}`}
          className="shrink-0"
        >
          {impersonating === h.user_id ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <>
              <LogIn className="w-3 h-3 mr-1" />
              Login
            </>
          )}
        </Button>
        <button
          type="button"
          onClick={() => handleOpenPublic(h.user_id, h.name)}
          disabled={generating === h.user_id}
          className="shrink-0 ml-1 p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"
          title="Open public view in new tab"
          data-testid={`switchboard-open-public-${h.user_id}`}
        >
          {generating === h.user_id ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ExternalLink className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          type="button"
          onClick={() => handleCopyPublic(h.user_id, h.name)}
          disabled={generating === h.user_id}
          className="shrink-0 p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"
          title="Copy public-view link"
          data-testid={`switchboard-copy-public-link-${h.user_id}`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </li>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5" data-testid="admin-switchboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-600" />
          <h3 className="font-semibold text-sm">Admin Switchboard</h3>
          <span className="text-xs text-gray-400">
            ({pilots.length} pilots · {demos.length} demos)
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          data-testid="switchboard-refresh-btn"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by name, email, category, dashboard"
        className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg mb-3"
        data-testid="switchboard-filter-input"
      />

      {loading ? (
        <div className="text-center py-8 text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          Loading accounts...
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pilots section */}
          <div data-testid="switchboard-pilots-section">
            <div className="flex items-center gap-2 mb-1.5 px-1">
              <Users className="w-3.5 h-3.5 text-emerald-600" />
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                Pilots
              </h4>
              <span className="text-[10px] text-gray-400">({pilots.length})</span>
            </div>
            {pilots.length === 0 ? (
              <p className="text-xs text-gray-400 px-1 py-2">No pilots match.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg bg-emerald-50/40 px-2">
                {pilots.map(renderRow)}
              </ul>
            )}
          </div>

          {/* Demos section */}
          <div data-testid="switchboard-demos-section">
            <div className="flex items-center gap-2 mb-1.5 px-1">
              <Sparkles className="w-3.5 h-3.5 text-purple-600" />
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">
                Demos
              </h4>
              <span className="text-[10px] text-gray-400">({demos.length})</span>
            </div>
            {demos.length === 0 ? (
              <p className="text-xs text-gray-400 px-1 py-2">No demos match.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg bg-purple-50/40 px-2 max-h-[420px] overflow-y-auto">
                {demos.map(renderRow)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSwitchboard;
